<template>
  <tr>
    <td>
      <tool-chip-count
        v-if="showChipCount"
        :count="callsheetAnalysesCount"
        :tooltip="`${callsheetAnalysesCount} items discovered`"
        right
      >
      </tool-chip-count>
      <v-tooltip
        v-else-if="progress"
        right
        :color="progressTooltip.color"
        content-class="caption"
        transition="slide-x-transition"
      >
        <template v-slot:activator="{ on }">
          <v-icon v-on="on" small :color="progressIconColor">
            {{ progressIcon }}
          </v-icon>
        </template>
        <span v-if="progressTooltip.tip">{{ progressTooltip.tip }}</span>
      </v-tooltip>
    </td>
    <td class="fid-overflow-word">
      {{ item.originalName }}
    </td>
    <td>
      {{ item.comment }}
    </td>
    <td>
      {{ item.uploaded }}
    </td>
    <td>
      <v-tooltip
        left
        color="secondary"
        content-class="caption"
        transition="slide-x-transition"
      >
        <template v-slot:activator="{ on }">
          <v-btn v-on="on" small icon :href="getArtworkRef" target="_blank" :disabled="!Boolean(getArtworkRef)">
            <v-icon small>{{ getIcon('showinbrowser') }}</v-icon>
          </v-btn>
        </template>
        <span>Show in browser</span>
      </v-tooltip>

      <v-tooltip
        left
        color="secondary"
        content-class="caption"
        transition="slide-x-transition"
      >
        <template v-slot:activator="{ on }">
          <v-btn v-on="on"
            small icon
            :href="getArtworkRefDownload"
            :disabled="!Boolean(getArtworkRefDownload)">
            <v-icon small>{{ getIcon('download') }}</v-icon>
          </v-btn>
        </template>
        <span>Download original</span>
      </v-tooltip>

      <v-tooltip
        left
        color="secondary"
        content-class="caption"
        transition="slide-x-transition"
      >
        <template v-slot:activator="{ on }">
          <v-btn v-on="on" small icon @click="editItem" :disabled="!clickable">
            <v-icon small>{{ getIcon('edit') }}</v-icon>
          </v-btn>
        </template>
        <span>Edit comment</span>
      </v-tooltip>

      <v-tooltip
        left
        color="secondary"
        content-class="caption"
        transition="slide-x-transition"
      >
        <template v-slot:activator="{ on }">
          <v-btn
            v-on="on"
            small
            icon
            @click="deleteItem"
            :disabled="!clickable"
          >
            <v-icon small>{{ getIcon('delete') }}</v-icon>
          </v-btn>
        </template>
        <span>Delete callsheet</span>
      </v-tooltip>

      <v-tooltip
        left
        color="secondary"
        content-class="caption"
        transition="slide-x-transition"
      >
        <template v-slot:activator="{ on }">
          <v-btn v-on="on" small icon @click="rerunItem" :disabled="!clickable">
            <v-icon small>{{ getIcon('rerun') }}</v-icon>
          </v-btn>
        </template>
        <span>Redo callsheet analysis</span>
      </v-tooltip>

      <v-tooltip
        left
        color="secondary"
        content-class="caption"
        transition="slide-x-transition"
      >
        <template v-slot:activator="{ on }">
          <v-btn
            v-on="on"
            small
            icon
            @click="showItemAnalyses"
            :disabled="!isCallsheetAnalyses || !clickable"
          >
            <v-icon small>keyboard_arrow_down</v-icon>
          </v-btn>
        </template>
        <span>Show analysis detail</span>
      </v-tooltip>
    </td>
  </tr>
</template>
<script>
/**
 * this displays a row of a call sheet
 * and polls progress so it can update its icon for status with the callsheetanalysisprogress query
 * when expanded it emits a showanalyses  event
 */
import Toolchipcount from '@/components/Toolchipcount'
import { getIcon, getIconColor, settings } from '@/js/store'
import { SearchUtils } from '@/js'
import { CallsheetAnalysisProgress } from '@/gql'
export default {
  components: {
    'tool-chip-count': Toolchipcount,
  },
  props: {
    item: Object,
    committing: Boolean,
  },
  watch: {
    progress() {
      this.setPollInterval()
    },
  },
  data: () => ({
    CallsheetAnalysisProgress: null,
    pollInterval: 0,
    startingToPoll: false,
  }),
  methods: {
    setPollInterval(start) {
      const val = this.progress
      this.startingToPoll = start
      // keeps polling on progress till its done or an error
      if (this.startingToPoll || (val && val.status !== 'done' && !val.error)) {
        // make the first one a bit quicker
        const pollInterval = this.startingToPoll ? 3000 : 15000
        if (!this.pollInterval) {
          this.$apollo.queries.CallsheetAnalysisProgress.startPolling(
            pollInterval,
          )
        }
        this.pollInterval = pollInterval
      } else {
        // heres a potential transition, so we do a refetch on how many there are
        if (this.pollInterval) {
          this.$emit('refetch')
        }
        this.pollInterval = 0
        this.$apollo.queries.CallsheetAnalysisProgress.stopPolling()
      }
    },
    showItemAnalyses() {
      this.$emit('showanalyses', {
        item: this.item,
      })
    },
    getIcon(name) {
      return getIcon(name)
    },
    editItem() {
      this.$emit('edit', {
        item: this.item,
      })
    },
    deleteItem() {
      this.$emit('delete', {
        item: this.item,
      })
    },
    rerunItem() {
      this.$emit('rerun', {
        item: this.item,
      })
      this.setPollInterval(true)
    },
  },

  computed: {
    getArtworkRef() {
      return SearchUtils.getArtworkRef({ base: this.item, download: false })
    },
    getArtworkRefDownload() {
      const t= SearchUtils.getArtworkRef({ base: this.item, download: true })
      return t
    },
    clickable() {
      return !this.committing && this.progressRunnable
    },
    // this is the progress record showing how far along the callsheet analysis is
    progress() {
      return this.CallsheetAnalysisProgress
    },
    // the callsheet id
    id() {
      return this.item && this.item.id
    },

    // are there any callsheet analyses available
    isCallsheetAnalyses() {
      return this.item && this.item.CallsheetAnalyses
    },
    // how many are there
    callsheetAnalysesCount() {
      const isca = this.isCallsheetAnalyses
      return (isca && isca[0] && isca[0].countRows) || 0
    },
    // if something went really wrong, perhaps there no error properly recorded
    progressExpired() {
      if (!this.progress || this.progress.success || this.progress.error)
        return false
      return SearchUtils.progressExpired({
        runID: this.progress.runID,
        timeout: settings.callsheets.analysisProgress.timeout,
      })
    },
    // has there been a progress error
    progressError() {
      return (
        (this.progress && this.progress.error && !this.progressSuccess) ||
        this.progressExpired
      )
    },
    progressSuccess() {
      return this.progress && this.progress.success
    },
    progressRunning() {
      return this.progress && !this.progressError && !this.progressSuccess
    },
    progressRunnable() {
      return !this.progress || this.progressError || this.progressSuccess
    },
    // show progress from status
    // starting to poll means the analysis is starting and hasnt had an iupdate yet
    progressIcon() {
      if (!this.progress) return null
      return this.progressError
        ? getIcon(this.progressExpired ? 'expired' : 'error')
        : getIcon(this.startingToPoll ? 'waiting' : this.progress.status)
    },
    progressTooltip() {
      if (!this.progress)
        return {
          color: 'primary',
          tip: null,
        }
      if (this.progressExpired)
        return {
          color: 'error',
          tip: 'Expired without finishing - try again',
        }
      if (this.startingToPoll)
        return {
          color: 'accent',
          tip: 'Waiting to analyze',
        }
      if (this.progressError)
        return {
          color: 'error',
          tip: this.progress.error,
        }
      return {
        color: 'secondary',
        tip: this.progress.status,
      }
    },
    progressIconColor() {
      if (!this.progress) return null
      return this.progressError
        ? getIcon(this.progressExpired ? 'expired' : 'error')
        : getIconColor(this.startingToPoll ? 'waiting' : this.progress.status)
    },
    showChipCount() {
      return (
        (!this.progress || this.progress.success) && this.isCallsheetAnalyses
      )
    },

    xs() {
      return this.$vuetify.breakpoint.xs
    },
    errorIcon() {
      return getIcon('error')
    },
  },
  apollo: {
    CallsheetAnalysisProgress: {
      // TODO we need more than this.id - no need to poll if we already have a result
      skip() {
        return !this.id
      },
      fetchPolicy: 'no-cache',
      query: CallsheetAnalysisProgress,
      variables() {
        return {
          id: this.id,
        }
      },
    },
  },
}
</script>
